import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AlertTriangle, Loader2 } from 'lucide-react';

interface CancelRideDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bookingId: number;
  onConfirm: (bookingId: number, remarks: string) => void;
  isLoading?: boolean;
}

const CancelRideDialog: React.FC<CancelRideDialogProps> = ({
  open,
  onOpenChange,
  bookingId,
  onConfirm,
  isLoading = false,
}) => {
  const [remarks, setRemarks] = useState('');
  const [error, setError] = useState('');

  const handleConfirm = () => {
    if (!remarks.trim()) {
      setError('Remarks are required to end a ride');
      return;
    }
    setError('');
    onConfirm(bookingId, remarks.trim());
  };

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) {
      setRemarks('');
      setError('');
    }
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-destructive" />
            End Ride #{bookingId}
          </DialogTitle>
          <DialogDescription>
            This will cancel the ongoing ride. Please provide a reason for ending this ride.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="remarks">Remarks *</Label>
            <Textarea
              id="remarks"
              placeholder="Enter reason for ending this ride..."
              value={remarks}
              onChange={(e) => {
                setRemarks(e.target.value);
                if (error) setError('');
              }}
              rows={3}
              className={error ? 'border-destructive' : ''}
            />
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Ending...
              </>
            ) : (
              'End Ride'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CancelRideDialog;
